import React from 'react';
import StatusBadge from './StatusBadge';

/**
 * TranscriptCard - Compact card for transcript list with improved accessibility
 */
const TranscriptCard = ({
  transcript,
  isSelected,
  onClick,
  onRegenerateSummary,
  regenerating,
  formatDate,
  formatDuration
}) => {
  const hasContent = transcript.has_content;
  const hasSummary = transcript.has_summary;
  const summarySource = transcript.summary_source;
  const analysisStatus = transcript.analysis_status;

  const formatSessionDateTime = (dateStr) => {
    if (!dateStr) return 'Unknown session time';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return formatDate(dateStr);
    return date.toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const extractParticipantNames = (transcriptContent) => {
    if (!transcriptContent || typeof transcriptContent !== 'string') return [];

    const genericNamePattern = /^(speaker|participant|guest|unknown|meeting|user|attendee)(\s*\d+)?$/i;
    const excludedNames = new Set(['me', 'you', 'host']);
    const uniqueNames = new Set();
    const lines = transcriptContent.split(/\r?\n/);

    for (const line of lines) {
      const match = line.match(/^\s*([^:\n]{1,60})\s*:\s+\S/);
      if (!match) continue;

      const candidate = match[1].trim().replace(/^["'[]+|["'\]]+$/g, '');
      const normalizedCandidate = candidate.toLowerCase();

      if (!candidate || candidate.length < 2) continue;
      if (excludedNames.has(normalizedCandidate)) continue;
      if (genericNamePattern.test(candidate)) continue;
      if (!/[a-z]/i.test(candidate)) continue;

      uniqueNames.add(candidate);
    }

    return Array.from(uniqueNames);
  };

  const getAvailabilityState = () => {
    if (hasContent) return 'available';
    if (transcript.sync_status === 'FAILED') return 'failed';
    return 'processing';
  };

  const availabilityState = getAvailabilityState();
  const participantNames = extractParticipantNames(transcript.transcript_content);
  const participantSubtitle = participantNames.length > 0
    ? participantNames.slice(0, 2).join(', ') + (participantNames.length > 2 ? ` +${participantNames.length - 2} more` : '')
    : transcript.meeting_title || transcript.meeting_code || 'Participants unavailable';

  return (
    <div
      className={`transcript-card ${isSelected ? 'selected' : ''} transcript-card--${availabilityState}`}
      onClick={() => onClick(transcript)}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick(transcript)}
      aria-label={`${formatSessionDateTime(transcript.start_time)}, ${participantSubtitle}, ${formatDuration(transcript.start_time, transcript.end_time)}, ${availabilityState === 'available' ? 'transcript available' : 'transcript not available'}`}
    >
      <div className="transcript-card-header">
        <div className="transcript-card-badges">
          {availabilityState === 'available' ? (
            <StatusBadge variant="success" size="small">Available</StatusBadge>
          ) : availabilityState === 'processing' ? (
            <StatusBadge variant="warning" size="small">Processing</StatusBadge>
          ) : (
            <StatusBadge variant="error" size="small">Failed</StatusBadge>
          )}

          {hasSummary && summarySource === 'GOOGLE' && (
            <StatusBadge variant="info" size="small">Google Notes</StatusBadge>
          )}
          {hasSummary && summarySource === 'AI_GENERATED' && (
            <StatusBadge variant="neutral" size="small">AI</StatusBadge>
          )}
          {hasSummary && summarySource === 'AI_GENERATED' && analysisStatus === 'COMPLETE' && (
            <StatusBadge variant="success" size="small">Complete</StatusBadge>
          )}
          {hasSummary && summarySource === 'AI_GENERATED' && analysisStatus === 'PARTIAL' && (
            <StatusBadge variant="warning" size="small">Partial</StatusBadge>
          )}
          {hasSummary && summarySource === 'AI_GENERATED' && analysisStatus === 'FAILED' && (
            <StatusBadge variant="error" size="small">Failed</StatusBadge>
          )}
        </div>

        <div className="transcript-card-actions">
          <button
            type="button"
            className="transcript-regenerate-btn"
            disabled={!hasContent || regenerating}
            title="Refresh notes from Google"
            aria-label="Refresh notes"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasContent || regenerating) return;
              onRegenerateSummary && onRegenerateSummary(transcript.id);
            }}
          >
            {regenerating ? (
              <span className="btn-spinner-small"></span>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="transcript-card-title">
        {formatSessionDateTime(transcript.start_time)}
      </div>
      <div className="transcript-card-subtitle" title={participantSubtitle}>
        {participantSubtitle}
      </div>

      <div className="transcript-card-meta">
        <span className="duration">
          {formatDuration(transcript.start_time, transcript.end_time)}
        </span>
        {transcript.meeting_code && (
          <>
            <span className="meta-separator">|</span>
            <span className="meeting-code-hint">{transcript.meeting_code}</span>
          </>
        )}
        {transcript.linked_student_uuid && (
          <>
            <span className="meta-separator">|</span>
            <span className="student-linked" title="Student linked">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Linked
            </span>
          </>
        )}
      </div>

      {availabilityState === 'processing' && (
        <div className="transcript-card-hint">
          Transcripts take 15-45 min after meeting ends
        </div>
      )}
    </div>
  );
};

export default TranscriptCard;
